import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout';
import { Button, Input, Card } from '@/components/ui';
import { useEffectiveBusinessId } from '@/hooks';
import { useSuppliers } from '../hooks';
import { SupplierCard } from '../components';
import { createSupplier } from '../api/suppliersApi';
import { formatPhoneInput } from '@/lib/phoneUtils';
import { CURRENCY_OPTIONS, DEFAULT_CURRENCY } from '../types';
import type { Currency } from '../types';

export function SuppliersListPage() {
  const effectiveBusinessId = useEffectiveBusinessId();
  const navigate = useNavigate();
  const {
    suppliers,
    isLoading,
    error,
    refetch,
    search,
    clearSearch,
    isSearching,
  } = useSuppliers({
    businessId: effectiveBusinessId,
  });

  const [searchTerm, setSearchTerm] = useState('');

  // New supplier form state
  const [isCreating, setIsCreating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [newSupplierForm, setNewSupplierForm] = useState({
    name: '',
    contactEmail: '',
    phone: '',
    defaultCurrency: DEFAULT_CURRENCY as Currency,
    website: '',
    notes: '',
  });

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchTerm(value);
      if (value.trim()) {
        search(value);
      } else {
        clearSearch();
      }
    },
    [search, clearSearch]
  );

  const handleEdit = (supplierId: string) => {
    navigate(`/suppliers/${supplierId}?edit=true`);
  };

  const handleStartCreate = () => {
    setIsCreating(true);
    setCreateError(null);
    setNewSupplierForm({
      name: '',
      contactEmail: '',
      phone: '',
      defaultCurrency: DEFAULT_CURRENCY,
      website: '',
      notes: '',
    });
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
    setCreateError(null);
  };

  const handleCreateSupplier = async () => {
    if (!effectiveBusinessId) return;
    if (!newSupplierForm.name.trim()) {
      setCreateError('Supplier name is required');
      return;
    }

    setIsSubmitting(true);
    setCreateError(null);

    try {
      await createSupplier({
        business_id: effectiveBusinessId,
        name: newSupplierForm.name.trim(),
        contact_email: newSupplierForm.contactEmail.trim() || null,
        phone: newSupplierForm.phone.trim() || null,
        default_currency: newSupplierForm.defaultCurrency,
        website: newSupplierForm.website.trim() || null,
        notes: newSupplierForm.notes.trim() || null,
      });
      setIsCreating(false);
      refetch();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to create supplier';
      setCreateError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePhoneChange = (value: string) => {
    setNewSupplierForm((prev) => ({
      ...prev,
      phone: formatPhoneInput(value),
    }));
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-secondary-900">Suppliers</h1>
            <p className="text-sm text-secondary-500 mt-1">
              Manage your suppliers and their locations
            </p>
          </div>
          <Button variant="primary" onClick={handleStartCreate}>
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            New Supplier
          </Button>
        </div>

        {/* Search */}
        <div className="mb-6">
          <Input
            name="search"
            placeholder="Search by supplier name..."
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="max-w-md"
          />
        </div>

        {/* Errors */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Create Supplier Form */}
        {isCreating && (
          <Card className="mb-6">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-secondary-900 mb-4">
                New Supplier
              </h2>

              {createError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">{createError}</p>
                </div>
              )}

              <div className="space-y-4">
                {/* Supplier Name */}
                <Input
                  label="Supplier Name"
                  name="name"
                  value={newSupplierForm.name}
                  onChange={(e) =>
                    setNewSupplierForm((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  placeholder="Home Depot"
                  disabled={isSubmitting}
                  required
                />

                {/* Contact Info Row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    label="Contact Email (Optional)"
                    name="contactEmail"
                    type="email"
                    value={newSupplierForm.contactEmail}
                    onChange={(e) =>
                      setNewSupplierForm((prev) => ({
                        ...prev,
                        contactEmail: e.target.value,
                      }))
                    }
                    placeholder="contact@supplier.com"
                    disabled={isSubmitting}
                  />
                  <Input
                    label="Phone (Optional)"
                    name="phone"
                    type="tel"
                    value={newSupplierForm.phone}
                    onChange={(e) => handlePhoneChange(e.target.value)}
                    placeholder="(416) 555-1234"
                    disabled={isSubmitting}
                  />
                </div>

                {/* Website and Currency Row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    label="Website (Optional)"
                    name="website"
                    type="url"
                    value={newSupplierForm.website}
                    onChange={(e) =>
                      setNewSupplierForm((prev) => ({
                        ...prev,
                        website: e.target.value,
                      }))
                    }
                    placeholder="https://www.supplier.com"
                    disabled={isSubmitting}
                  />
                  <div className="w-full">
                    <label htmlFor="defaultCurrency" className="input-label">
                      Default Currency
                    </label>
                    <select
                      id="defaultCurrency"
                      name="defaultCurrency"
                      value={newSupplierForm.defaultCurrency}
                      onChange={(e) =>
                        setNewSupplierForm((prev) => ({
                          ...prev,
                          defaultCurrency: e.target.value as Currency,
                        }))
                      }
                      className="input-field"
                      disabled={isSubmitting}
                    >
                      {CURRENCY_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label htmlFor="notes" className="input-label">
                    Notes (Optional)
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={newSupplierForm.notes}
                    onChange={(e) =>
                      setNewSupplierForm((prev) => ({
                        ...prev,
                        notes: e.target.value,
                      }))
                    }
                    placeholder="Internal notes about this supplier..."
                    rows={3}
                    className="input-field resize-none"
                    disabled={isSubmitting}
                  />
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={handleCancelCreate}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleCreateSupplier}
                    isLoading={isSubmitting}
                  >
                    Create Supplier
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Loading State */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center gap-3">
              <svg
                className="animate-spin w-5 h-5 text-primary-600"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span className="text-secondary-600">Loading suppliers...</span>
            </div>
          </div>
        ) : suppliers.length === 0 ? (
          /* Empty State */
          <div className="text-center py-12">
            <svg
              className="w-12 h-12 text-secondary-400 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
            <h3 className="text-lg font-medium text-secondary-900 mb-2">
              {searchTerm ? 'No suppliers found' : 'No suppliers yet'}
            </h3>
            <p className="text-secondary-500 mb-6">
              {searchTerm
                ? 'Try adjusting your search terms'
                : 'Add your first supplier to get started'}
            </p>
            {!searchTerm && (
              <Button variant="primary" onClick={handleStartCreate}>
                Add Supplier
              </Button>
            )}
          </div>
        ) : (
          /* Supplier Grid */
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {suppliers.map((supplier) => (
              <SupplierCard
                key={supplier.id}
                supplier={supplier}
                onEdit={() => handleEdit(supplier.id)}
              />
            ))}
          </div>
        )}

        {/* Searching indicator */}
        {isSearching && (
          <div className="fixed bottom-4 right-4 bg-white px-4 py-2 rounded-lg shadow-lg border border-secondary-200">
            <div className="flex items-center gap-2">
              <svg
                className="animate-spin w-4 h-4 text-primary-600"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span className="text-sm text-secondary-600">Searching...</span>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
